import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight, Download } from "lucide-react";

/**
 * Visualizador de mídia — tipo galeria estilo Instagram.
 *
 * Layout: um "track" horizontal contendo todos os slides lado a lado.
 * A posição do track é sempre `-index * width + dragX`.
 * - Drag: atualiza dragX em tempo real (sem transition)
 * - Release: decide novo index, anima o track com easing, no onTransitionEnd
 *   zera o dragX (que já estará compensado pelo novo index).
 */
export default function MediaViewer({ items = [], startIndex = 0, open, onClose }) {
    const [index, setIndex] = useState(startIndex);
    const [dragX, setDragX] = useState(0);
    const [animating, setAnimating] = useState(false);
    const [width, setWidth] = useState(0);
    const containerRef = useRef(null);
    const touchStart = useRef(null);

    // Reset index quando abre
    useEffect(() => {
        if (open) {
            setIndex(Math.max(0, Math.min(startIndex, items.length - 1)));
            setDragX(0);
            setAnimating(false);
        }
    }, [open, startIndex, items.length]);

    // Mede a largura do container
    useLayoutEffect(() => {
        if (!open || !containerRef.current) return undefined;
        const measure = () => {
            if (containerRef.current) setWidth(containerRef.current.clientWidth);
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(containerRef.current);
        window.addEventListener("orientationchange", measure);
        return () => {
            ro.disconnect();
            window.removeEventListener("orientationchange", measure);
        };
    }, [open]);

    const total = items.length;

    const goTo = useCallback((target) => {
        if (target < 0 || target > total - 1 || target === index) {
            // rebound pro mesmo index
            setAnimating(true);
            setDragX(0);
            return;
        }
        setAnimating(true);
        setIndex(target);
        setDragX(0);
    }, [index, total]);

    const next = useCallback(() => goTo(index + 1), [goTo, index]);
    const prev = useCallback(() => goTo(index - 1), [goTo, index]);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") onClose?.();
            else if (e.key === "ArrowRight") next();
            else if (e.key === "ArrowLeft") prev();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, next, prev, onClose]);

    // Touch handlers — swipe horizontal com resistência nas bordas
    const onTouchStart = (e) => {
        if (total <= 1) return;
        setAnimating(false);
        touchStart.current = {
            x: e.touches[0].clientX,
            y: e.touches[0].clientY,
            t: Date.now(),
            axis: null,
        };
    };

    const onTouchMove = (e) => {
        if (!touchStart.current || total <= 1) return;
        const dx = e.touches[0].clientX - touchStart.current.x;
        const dy = e.touches[0].clientY - touchStart.current.y;

        if (!touchStart.current.axis) {
            if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
            touchStart.current.axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
        }
        if (touchStart.current.axis !== "x") return;

        // Resistência nas pontas (puxar além do primeiro/último)
        let adjusted = dx;
        if ((index === 0 && dx > 0) || (index === total - 1 && dx < 0)) {
            adjusted = dx * 0.3;
        }
        setDragX(adjusted);
    };

    const onTouchEnd = () => {
        if (!touchStart.current || total <= 1) {
            touchStart.current = null;
            return;
        }
        const axis = touchStart.current.axis;
        const dt = Math.max(1, Date.now() - touchStart.current.t);
        touchStart.current = null;

        if (axis !== "x") {
            setDragX(0);
            return;
        }

        const velocity = Math.abs(dragX) / dt; // px/ms
        const threshold = Math.min(90, width * 0.2);
        const shouldSwipe = Math.abs(dragX) > threshold || velocity > 0.5;

        if (shouldSwipe && dragX < 0 && index < total - 1) goTo(index + 1);
        else if (shouldSwipe && dragX > 0 && index > 0) goTo(index - 1);
        else goTo(index);
    };

    const onTransitionEnd = () => setAnimating(false);

    if (!open || total === 0) return null;

    const current = items[index];
    const trackX = width > 0 ? -(index * width) + dragX : 0;

    return (
        <div
            className="fixed inset-0 z-[200] bg-black/95 flex flex-col fade-in"
            style={{ margin: 0, top: 0, left: 0, right: 0, bottom: 0 }}
            data-testid="media-viewer"
        >
            {/* Header */}
            <div className="flex items-center justify-between px-3 md:px-4 py-2 md:py-3 border-b border-white/10 bg-black/50 backdrop-blur-sm shrink-0">
                <div className="flex items-center gap-3 min-w-0">
                    <span className="font-mono text-xs text-white/80 tabular-nums">
                        {index + 1} / {total}
                    </span>
                    <span className="text-sm text-white/90 truncate">{current.filename}</span>
                </div>
                <div className="flex items-center gap-1">
                    <a
                        href={current.url}
                        download={current.filename}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 rounded-sm text-white/80 hover:text-white hover:bg-white/10 transition-colors"
                        data-testid="viewer-download"
                        aria-label="Baixar"
                    >
                        <Download className="w-5 h-5" />
                    </a>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-sm text-white/80 hover:text-white hover:bg-white/10 transition-colors"
                        data-testid="viewer-close"
                        aria-label="Fechar"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Viewport do carrossel */}
            <div
                ref={containerRef}
                className="flex-1 relative overflow-hidden min-h-0 select-none"
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
            >
                {width > 0 && (
                    <div
                        className="absolute top-0 bottom-0 left-0 flex"
                        style={{
                            width: `${total * width}px`,
                            transform: `translate3d(${trackX}px, 0, 0)`,
                            transition: animating
                                ? "transform 300ms cubic-bezier(0.22, 0.61, 0.36, 1)"
                                : "none",
                            willChange: "transform",
                        }}
                        onTransitionEnd={onTransitionEnd}
                    >
                        {items.map((item, i) => (
                            <Slide
                                key={`${item.url}-${i}`}
                                item={item}
                                width={width}
                                active={i === index}
                            />
                        ))}
                    </div>
                )}

                {/* Setas desktop */}
                {total > 1 && (
                    <>
                        <button
                            onClick={prev}
                            disabled={index === 0}
                            className="hidden md:flex absolute left-3 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/60 hover:bg-black/80 text-white transition-colors z-10 disabled:opacity-30 disabled:cursor-not-allowed"
                            data-testid="viewer-prev"
                            aria-label="Anterior"
                        >
                            <ChevronLeft className="w-6 h-6" />
                        </button>
                        <button
                            onClick={next}
                            disabled={index === total - 1}
                            className="hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/60 hover:bg-black/80 text-white transition-colors z-10 disabled:opacity-30 disabled:cursor-not-allowed"
                            data-testid="viewer-next"
                            aria-label="Próximo"
                        >
                            <ChevronRight className="w-6 h-6" />
                        </button>
                    </>
                )}
            </div>

            {/* Indicador mobile */}
            {total > 1 && (
                <div className="md:hidden py-3 flex justify-center gap-1.5 shrink-0">
                    {items.map((_, i) => (
                        <span
                            key={i}
                            className={`h-1.5 rounded-full transition-all ${
                                i === index ? "w-6 bg-primary" : "w-1.5 bg-white/30"
                            }`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function Slide({ item, width, active }) {
    const isVid = (item?.content_type || "").startsWith("video/");
    return (
        <div
            className="flex items-center justify-center shrink-0 h-full p-3 md:p-8"
            style={{ width: `${width}px` }}
        >
            {isVid ? (
                <video
                    src={item.url}
                    controls={active}
                    className="max-w-full max-h-full rounded-sm"
                    data-testid={active ? "viewer-video" : undefined}
                />
            ) : (
                <img
                    src={item.url}
                    alt={item.filename}
                    className="max-w-full max-h-full object-contain rounded-sm select-none pointer-events-none"
                    draggable={false}
                    data-testid={active ? "viewer-image" : undefined}
                />
            )}
        </div>
    );
}
